import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  normalizeUploadConfig,
  prepareUploadFile,
  validateUploadFile,
} from "../../utils/fileUploadConfig";

/** True if the file is HEIC/HEIF (browsers often can't preview it in img). */
function isHeicFile(file) {
  if (!file) return false;
  return (
    /\.heic$/i.test(file.name) ||
    /\.heif$/i.test(file.name) ||
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    file.type === "image/heic-sequence"
  );
}

/**
 * Convert HEIC/HEIF file to a JPEG Blob for in-browser preview only.
 * Uses dynamic import so heic2any runs only in the browser when needed.
 * Returns object URL string or null on failure.
 */
async function convertHeicToPreviewUrl(file) {
  if (typeof window === "undefined") return null;
  try {
    const heic2any = (await import("heic2any")).default;
    const blob = file instanceof File ? file : file.slice(0, file.size, file.type || "image/heic");
    let result = await heic2any({
      blob,
      toType: "image/jpeg",
    });
    const outBlob = Array.isArray(result) ? result[0] : result;
    if (outBlob && outBlob instanceof Blob) {
      return URL.createObjectURL(outBlob);
    }
    result = await heic2any({
      blob,
      toType: "image/png",
    });
    const pngBlob = Array.isArray(result) ? result[0] : result;
    if (pngBlob && pngBlob instanceof Blob) {
      return URL.createObjectURL(pngBlob);
    }
  } catch (err) {
    console.warn("HEIC preview conversion failed:", err);
  }
  return null;
}

/** True if the stored fileData is HEIC/HEIF (data URL). */
function isHeicDataUrl(fileData) {
  if (typeof fileData !== "string") return false;
  return (
    fileData.startsWith("data:image/heic") ||
    fileData.startsWith("data:image/heif")
  );
}

const runtimePreviewCache = new Map();
const RUNTIME_PREVIEW_CACHE_LIMIT = 8;

const previewCacheKey = (question, file) => {
  if (file?.upload_reference) return `receipt:${file.upload_reference}`;
  return `${question?.release_node_id || question?.source_id || question?.id || ""}:${file?.name || file?.filename || ""}:${file?.size || file?.fileSize || 0}`;
};

const isPreviewableImage = (file) => (
  String(file?.type || "").startsWith("image/")
  || /\.(avif|bmp|gif|jpe?g|png|webp)$/i.test(
    String(file?.name || file?.filename || ""),
  )
);

const revokePreviewUrl = (url) => {
  if (String(url || "").startsWith("blob:")) URL.revokeObjectURL(url);
};

const readImagePreview = (file) => new Promise((resolve, reject) => {
  if (!isPreviewableImage(file)) {
    resolve(null);
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("The image preview could not be created."));
  reader.readAsDataURL(file);
});

const rememberRuntimePreview = (key, url) => {
  if (!key || !url) return;
  const previous = runtimePreviewCache.get(key);
  if (previous && previous !== url) revokePreviewUrl(previous);
  runtimePreviewCache.delete(key);
  runtimePreviewCache.set(key, url);
  while (runtimePreviewCache.size > RUNTIME_PREVIEW_CACHE_LIMIT) {
    const oldestKey = runtimePreviewCache.keys().next().value;
    const oldestUrl = runtimePreviewCache.get(oldestKey);
    runtimePreviewCache.delete(oldestKey);
    if (oldestUrl) revokePreviewUrl(oldestUrl);
  }
};

const forgetRuntimePreview = (key) => {
  const url = runtimePreviewCache.get(key);
  runtimePreviewCache.delete(key);
  if (url) revokePreviewUrl(url);
};

const forgetRuntimePreviewUrl = (url) => {
  if (!url) return;
  for (const [key, cachedUrl] of runtimePreviewCache.entries()) {
    if (cachedUrl === url) forgetRuntimePreview(key);
  }
};

// ===== STYLED COMPONENTS =====

const Container = styled.div`
  margin-bottom: 24px;
`;

const QuestionLabel = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2d3748;
  font-size: 16px;
  line-height: 1.5;
`;

const QuestionDescription = styled.p`
  color: #718096;
  font-size: 14px;
  margin: 0 0 20px 0;
  line-height: 1.6;
`;

const UploadArea = styled.div`
  border: 2px dashed ${(props) => (props.$isDragOver ? "#667eea" : "#cbd5e0")};
  border-radius: 12px;
  padding: 48px 32px;
  text-align: center;
  background-color: ${(props) => (props.$isDragOver ? "#f7fafc" : "#fafbfc")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${(props) => (props.disabled ? "#cbd5e0" : "#667eea")};
    background-color: ${(props) => (props.disabled ? "#fafbfc" : "#f7fafc")};
  }

  @media (max-width: 768px) {
    padding: 40px 24px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  transition: all 0.3s ease;

  ${UploadArea}:hover & {
    background: #cbd5e0;
    transform: scale(1.05);
  }
`;

const UploadTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
`;

const UploadSubtext = styled.div`
  font-size: 14px;
  color: #718096;
  line-height: 1.5;
`;

const UploadMeta = styled.div`
  font-size: 13px;
  color: #a0aec0;
  margin-top: 12px;
  line-height: 1.4;
`;

const PreviewContainer = styled.div`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  position: relative;
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const FileName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
`;

const FileNameText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UploadedBadge = styled.span`
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #fed7d7;
    color: #c53030;
  }
`;

const PreviewImageContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #f7fafc;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const PreviewPlaceholder = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #718096;
  font-size: 14px;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
`;

const FileInfo = styled.div`
  font-size: 13px;
  color: #718096;
  margin-top: 12px;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  color: #22543d;
  padding: 16px 20px;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #9ae6b4;

  &:before {
    content: "✓";
    background: #48bb78;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: #c53030;
  padding: 16px 20px;
  border-radius: 8px;
  margin-top: 16px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid #feb2b2;

  &:before {
    content: "⚠️";
    font-size: 18px;
    flex-shrink: 0;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: fileUploadSpin 0.8s linear infinite;
  flex-shrink: 0;

  @keyframes fileUploadSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingMessage = styled.div`
  color: #667eea;
  font-size: 14px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  background: #edf2f7;
  border-radius: 8px;
`;

const ContinueHint = styled.div`
  background: #edf2f7;
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 12px;
  font-size: 13px;
  color: #4a5568;
  text-align: center;
`;

// ===== MAIN COMPONENT =====

const FileUpload = ({
  question,
  value,
  onChange,
  error,
  uploadFile,
  removeFile,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef();
  const lastClearedValueRef = useRef(null);
  /** Object URL for HEIC-converted preview; must be revoked when no longer needed */
  const previewObjectUrlRef = useRef(null);
  const currentPreviewKey = previewCacheKey(question, value);

  // Initialize from existing value
  useEffect(() => {
    // Only show as uploaded if we have VALID file data
    // Must have all required fields AND fileData must be a valid base64 string
    if (
      value &&
      typeof value === "object" &&
      value.filename &&
      typeof value.filename === "string" &&
      value.filename.trim() !== "" &&
      ((value.fileData &&
        typeof value.fileData === "string" &&
        value.fileData.startsWith("data:") &&
        value.fileSize &&
        typeof value.fileSize === "number" &&
        value.fileSize > 0) ||
        value.hasFileData)
    ) {
      // Runtime uploads intentionally hydrate from an opaque receipt, so the
      // browser can only preview them while the selected file's object URL is
      // still alive. Do not leave a stale/broken <img> after a remount.
      const hasInlinePreview = (
        value.fileData
        && typeof value.fileData === "string"
        && value.fileData.startsWith("data:")
      );
      if (hasInlinePreview && !isHeicDataUrl(value.fileData)) {
        setPreview(value.fileData);
      } else if (!previewObjectUrlRef.current) {
        const cachedPreview = runtimePreviewCache.get(currentPreviewKey);
        if (cachedPreview) {
          previewObjectUrlRef.current = cachedPreview;
          setPreview(cachedPreview);
        } else {
          setPreview(null);
        }
      }
      setUploadSuccess(true);
    } else {
      if (previewObjectUrlRef.current) {
        forgetRuntimePreviewUrl(previewObjectUrlRef.current);
        revokePreviewUrl(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      setPreview(null);
      setUploadSuccess(false);

      // If there's invalid data (not null/undefined), clear it ONCE
      if (
        value !== null &&
        value !== undefined &&
        value !== lastClearedValueRef.current
      ) {
        const isInvalidData =
          typeof value !== "object" ||
          !value.filename ||
          (!value.fileData && !value.hasFileData) ||
          (typeof value.fileData === "string" &&
            !value.fileData.startsWith("data:"));

        if (isInvalidData) {
          lastClearedValueRef.current = value;
          onChange(null);
        }
      }
    }
  }, [currentPreviewKey, onChange, value]);

  const handleFileSelect = async (file) => {
    if (!file) {
      onChange(null);
      setPreview(null);
      setUploadSuccess(false);
      setUploadError(null);
      return;
    }

    const validation = validateUploadFile(file, question.upload_config);
    if (!validation.valid) {
      onChange(null);
      setPreview(null);
      setUploadSuccess(false);
      setUploadError(validation.error);
      return;
    }

    // Treatment runtime uploads bytes first and stores only an opaque receipt.
    // Legacy questionnaires retain their established inline-data envelope.
    setIsProcessing(true);
    setUploadSuccess(false);
    setUploadError(null);

    try {
      const preparedFile = await prepareUploadFile(file);
      const preparedValidation = validateUploadFile(
        preparedFile,
        question.upload_config,
      );
      if (!preparedValidation.valid) {
        throw new Error(preparedValidation.error);
      }
      if (uploadFile) {
        const receipt = await uploadFile(question, preparedFile);
        if (previewObjectUrlRef.current) {
          forgetRuntimePreviewUrl(previewObjectUrlRef.current);
          revokePreviewUrl(previewObjectUrlRef.current);
          previewObjectUrlRef.current = null;
        }
        const previewUrl = await readImagePreview(preparedFile);
        if (previewUrl) {
          rememberRuntimePreview(
            previewCacheKey(question, receipt),
            previewUrl,
          );
        }
        onChange(receipt);
        setPreview(previewUrl);
        setIsProcessing(false);
        setUploadSuccess(true);
        return;
      }
      const reader = new FileReader();

      reader.onload = async (e) => {
        const fileData = {
          filename: preparedFile.name,
          fileSize: preparedFile.size,
          fileType: preparedFile.type,
          fileData: e.target.result, // Base64 data URL (original HEIC or image)
          lastModified: preparedFile.lastModified,
          uploadedAt: new Date().toISOString(),
        };

        let previewUrl = e.target.result;
        if (isHeicFile(preparedFile)) {
          if (previewObjectUrlRef.current) {
            revokePreviewUrl(previewObjectUrlRef.current);
            previewObjectUrlRef.current = null;
          }
          previewUrl = await convertHeicToPreviewUrl(preparedFile);
          if (previewUrl) {
            previewObjectUrlRef.current = previewUrl;
          }
        }

        onChange(fileData);
        setPreview(previewUrl);
        setIsProcessing(false);
        setUploadSuccess(true);
      };

      reader.onerror = (error) => {
        console.error("File read error:", error);
        // alert('Error reading file. Please try again.');
        onChange(null);
        setPreview(null);
        setIsProcessing(false);
        setUploadSuccess(false);
        setUploadError("We could not read that file. Please try again.");
      };

      reader.readAsDataURL(preparedFile);
    } catch (error) {
      console.error("File processing error:", error);
      // Runtime uploads are already persisted remotely before the receipt is
      // returned. Keep the current receipt/value intact on a transient error
      // so a retry does not require selecting a different file or lose the
      // previously uploaded file.
      if (uploadFile) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setIsProcessing(false);
        setUploadSuccess(Boolean(value));
        setUploadError(
          error?.message || "We could not upload that file. Please try again.",
        );
        return;
      }
      // Legacy inline uploads retain their established error behavior.
      onChange(null);
      setPreview(null);
      setIsProcessing(false);
      setUploadSuccess(false);
      setUploadError(
        error?.message || "We could not process that file. Please try again.",
      );
    }
  };

  const handleClick = () => {
    if (!isProcessing) {
      fileInputRef.current?.click();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isProcessing) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (!isProcessing) {
      const file = e.dataTransfer.files[0];
      handleFileSelect(file);
    }
  };

  const handleRemove = async () => {
    if (!isProcessing) {
      setIsProcessing(true);
      setUploadError(null);
      try {
        if (removeFile) await removeFile(question, value);
        forgetRuntimePreview(currentPreviewKey);
        if (previewObjectUrlRef.current) {
          revokePreviewUrl(previewObjectUrlRef.current);
          previewObjectUrlRef.current = null;
        }
        onChange(null);
        setPreview(null);
        setUploadSuccess(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (removeError) {
        setUploadError(
          removeError?.message
          || "We could not remove that file. Please try again.",
        );
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handlePreviewError = () => {
    if (previewObjectUrlRef.current) {
      revokePreviewUrl(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    forgetRuntimePreview(currentPreviewKey);
    setPreview(null);
  };

  const getDescription = () => {
    if (question.instructions) {
      return question.instructions;
    }
    const purpose = normalizeUploadConfig(question.upload_config).upload_type;
    const descriptions = {
      prescription: "Upload a clear copy of your prescription.",
      photo_id: "Upload a clear copy of your valid photo ID.",
      insurance: "Upload a clear copy of your insurance document.",
      insurance_card: "Upload a clear copy of your insurance card.",
      medical_record: "Upload the requested medical record.",
      medical_records: "Upload the requested medical records.",
      lab_results: "Upload a clear copy of your lab results.",
    };
    return descriptions[purpose] || "Upload the requested document.";
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const uploadConfig = normalizeUploadConfig(question.upload_config);

  return (
    <Container>
      {/* <QuestionLabel>{question.question_text}</QuestionLabel> */}

      <QuestionDescription>{getDescription()}</QuestionDescription>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept={uploadConfig.allowed_extensions.join(",")}
        onChange={(e) => handleFileSelect(e.target.files[0])}
        disabled={isProcessing}
      />

      {!value || isProcessing ? (
        <>
          <UploadArea
            $isDragOver={isDragOver}
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={isProcessing}
          >
            <UploadIcon>📤</UploadIcon>
            <UploadTitle>
              Upload your{" "}
              {uploadConfig.allowed_extensions
                .join(", ")
                .replace(/\./g, "")
                .toUpperCase()}{" "}
              file
            </UploadTitle>
            <UploadSubtext>
              Drag and drop or click to select a file
            </UploadSubtext>
            <UploadMeta>
              Maximum file size:{" "}
                {uploadConfig.max_file_size_mb} MB
              <br />
              Supported formats:{" "}
              {uploadConfig.allowed_extensions.join(", ").toUpperCase()}
            </UploadMeta>
          </UploadArea>

          {isProcessing && (
            <LoadingMessage>
              <LoadingSpinner aria-hidden="true" />
              Processing file...
            </LoadingMessage>
          )}
        </>
      ) : (
        <>
          <PreviewContainer>
            <PreviewHeader>
              <FileName>
                <FileNameText>📄 {value.filename}</FileNameText>
                <UploadedBadge>✓ Uploaded</UploadedBadge>
              </FileName>
              <RemoveButton
                type="button"
                onClick={handleRemove}
                disabled={isProcessing}
                title="Remove file"
              >
                ✕
              </RemoveButton>
            </PreviewHeader>

            {isPreviewableImage(value) && (
              <PreviewImageContainer>
                {preview ? (
                  <PreviewImage
                    src={preview}
                    alt="Preview"
                    key={preview}
                    onError={handlePreviewError}
                  />
                ) : (
                  <PreviewPlaceholder>
                    Preview not available for this format. File is ready.
                  </PreviewPlaceholder>
                )}
              </PreviewImageContainer>
            )}

            <FileInfo>{formatFileSize(value.fileSize)}</FileInfo>
          </PreviewContainer>

          {uploadSuccess && (
            <SuccessMessage>
              File ready. Click continue to submit it securely.
            </SuccessMessage>
          )}
        </>
      )}

      {(error || uploadError) && <ErrorMessage>{error || uploadError}</ErrorMessage>}
    </Container>
  );
};

export default FileUpload;
